/*
 * Marketing HTML transform.
 *
 * Our marketing pages live as standalone HTML files in src/marketing/. We
 * import them as raw strings (via Vite ?raw) and render them through
 * dangerouslySetInnerHTML so the existing markup and CSS stay 1:1 with what
 * was approved.
 *
 * Before injecting, we:
 *   1. Pull out the <style> blocks from <head> and prepend them to the body
 *      content (so they still apply inside the SPA).
 *   2. Strip <!doctype>, <html>, <head>, <body> wrappers — those aren't valid
 *      inside the <div> container we render into.
 *   3. Rewrite relative asset paths to absolute (`assets/x.png` → `/assets/x.png`)
 *      so they resolve from any route, not just /.
 *   4. Rewrite legacy file links (`index.html`, `privacy.html`, `support.html`)
 *      to the SPA route paths.
 */

export function prepareMarketingHtml(html: string): string {
  const styles = [...html.matchAll(/<style[^>]*>[\s\S]*?<\/style>/gi)]
    .map((m) => m[0])
    .join('\n');

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const bodyInner = bodyMatch ? bodyMatch[1] : html;

  let out = `${styles}\n${bodyInner}`;

  // Make asset paths absolute.
  out = out.replace(/(src|href)="assets\//g, '$1="/assets/');

  // Map legacy `.html` links onto our SPA routes.
  out = out.replace(/href="index\.html"/g, 'href="/"');
  out = out.replace(/href="\.\/index\.html"/g, 'href="/"');
  out = out.replace(/href="privacy\.html"/g, 'href="/privacy"');
  out = out.replace(/href="support\.html"/g, 'href="/support"');

  return out;
}

/**
 * Click-handler factory: when an <a> inside the dangerouslySetInnerHTML
 * container points at one of our SPA routes, hand the click off to React
 * Router so we don't do a full page reload.
 */
export function makeInternalLinkHandler(
  navigate: (to: string) => void,
): (e: React.MouseEvent<HTMLDivElement>) => void {
  return (e) => {
    // Modifier clicks → let the browser do its thing (open in new tab, etc.)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;

    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Same-page hash link — let browser handle smooth scroll.
    if (href.startsWith('#')) return;

    // External, mailto, tel — let browser handle.
    if (/^(https?:|mailto:|tel:)/i.test(href)) return;

    // Internal SPA route — intercept.
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };
}
